import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Lock, Mail, MoreVertical, Truck, AtSign } from 'lucide-react';
import { DropdownMenu } from '../components/ui/DropdownMenu';
import { API_BASE_URL } from '../config'

interface UserProfile {
  user_id: number;
  username: string;
  name: string;
  surname: string;
  email: string;
  credit_balance: number;
  credit_purchased: number;
  letter_sent: number;
  date_of_birth?: string;
  address_street?: string;
  address_town?: string;
  address_zipcode?: string;
  address_region?: string;
  address_state?: string;
}

interface MailItem {
  mail_id: number;
  created_time: string;
  scheduled_send_time: string | null;
  mail_status: string;
  mail_type: 'physical' | 'email';
}

interface PhysicalLetterPreview extends MailItem {
  mail_type: 'physical';
  letter_content: string;
  delivery_name: string;
  delivery_surname: string;
}

interface EmailPreview extends MailItem {
  mail_type: 'email';
  email_content: string;
  recipient_name: string;
  recipient_email: string;
  subject: string;
}

type MailPreview = PhysicalLetterPreview | EmailPreview;

const DashboardPage: React.FC = () => {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [mailItems, setMailItems] = useState<MailPreview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const { token, logout } = useAuth();
  const navigate = useNavigate();

  const isDeliveryTimePassed = (scheduledDate: string | null) => {
    if (!scheduledDate) return false;
    return new Date(scheduledDate) < new Date();
  };

  const calculateTimeRemaining = (scheduledDate: string | null) => {
    if (!scheduledDate) return 'Not scheduled';
    const now = new Date();
    const scheduled = new Date(scheduledDate);
    const diffTime = scheduled.getTime() - now.getTime();
    
    if (diffTime <= 0) return 'Delivery time passed';
    
    const days = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diffTime % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days === 0 && hours > 0) {
      return '1 day';
    }
    
    return `${days} days`;
  };

  const getColors = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return { bg: 'bg-gray-50', gradient: 'rgba(249, 250, 251,' };
      case 'frozen':
        return { bg: 'bg-blue-50', gradient: 'rgba(239, 246, 255,' };
      case 'travelling':
        return { bg: 'bg-red-50', gradient: 'rgba(254, 242, 242,' };
      case 'open':
        return { bg: 'bg-white', gradient: 'rgba(255, 255, 255,' };
      case 'sent':
        return { bg: 'bg-green-50', gradient: 'rgba(236, 253, 245,' };
      case 'failed':
        return { bg: 'bg-red-50', gradient: 'rgba(254, 242, 242,' };
      case 'draft':
        return { bg: 'bg-yellow-50', gradient: 'rgba(254, 252, 232,' };
      default:
        return { bg: 'bg-white', gradient: 'rgba(255, 255, 255,' };
    }
  };

  const fetchMail = async () => {
    if (!profile?.user_id) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/letters/userpreview/${profile.user_id}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch mail items');
      }

      const data = await response.json();
      setMailItems(data.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };


  const renderMailItem = (item: MailPreview) => {
    const deliveryPassed = isDeliveryTimePassed(item.scheduled_send_time);
    const colors = getColors(item.mail_status);
    const isEmail = item.mail_type === 'email';

    return (
      <div 
        key={item.mail_id} 
        className={`${colors.bg} overflow-hidden shadow rounded-lg relative group select-none
        }`}
      >
        <div className="p-5 relative">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <div className="text-sm text-gray-500">
                {new Date(item.created_time).toLocaleDateString()}
              </div>
              {isEmail ? (
                <AtSign className="w-4 h-4 text-blue-400" />
              ) : (
                <Mail className="w-4 h-4 text-indigo-400" />
                  
              )}
            </div>
            <div className="flex items-center space-x-2 relative z-30">
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                isEmail ? 'bg-blue-100 text-blue-800' : 'bg-indigo-100 text-indigo-800'
              }`}>
                {item.mail_status}
              </span>
              {deliveryPassed && (
                <DropdownMenu
                  items={[
                    {
                      label: `View ${isEmail ? 'Email' : 'Letter'}`,
                      onClick: () => {/* Add your view handler */}
                    },
                    {
                      label: 'Download PDF',
                      onClick: () => {/* Add your download handler */}
                    },
                    {
                      label: 'Share',
                      onClick: () => {/* Add your share handler */}
                    }
                  ]}
                />
              )}
            </div>
          </div>
          
          <div className="mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              To: {isEmail ? item.recipient_name : item.delivery_name}
            </h3>

          </div>
          
          <div className="relative">
            <p className="text-sm text-gray-600 line-clamp-3">
              {isEmail ? item.email_content : item.letter_content}
            </p>
          </div>

          <div className="absolute inset-0 pointer-events-none z-10" style={{
            background: `linear-gradient(to bottom, ${colors.gradient}0.0) 0%, ${colors.gradient}0.0) 50%, ${colors.gradient}1) 90%)`
          }} />
        </div>

        {!deliveryPassed ? (
          <div className={`absolute inset-0 ${colors.bg} opacity-0 group-hover:opacity-90 group-hover:backdrop-blur-lg transition-all duration-300 ease-in-out flex flex-col items-center justify-center space-y-2 z-20`}>
            <Lock className="w-12 h-12 text-black opacity-0 group-hover:opacity-80 transition-opacity duration-300 ease-in-out" />
            <p className="text-black text-sm font-medium opacity-0 group-hover:opacity-80 transition-opacity duration-300 ease-in-out text-center">
              Locked for others<br />
              {calculateTimeRemaining(item.scheduled_send_time)}
            </p>
          </div>
        ) : (
          <div 
            className={`absolute inset-0 ${colors.bg} opacity-0 group-hover:opacity-90 group-hover:backdrop-blur-lg transition-all duration-300 ease-in-out flex flex-col items-center justify-center space-y-2 cursor-pointer z-20`}
            onClick={() => {/* Add your read handler here */}}>
            {isEmail ? (
              <Mail className="w-12 h-12 text-blue-600 opacity-0 group-hover:opacity-80 transition-opacity duration-300 ease-in-out" />
            ) : (
              <svg 
                className="w-12 h-12 text-indigo-600 opacity-0 group-hover:opacity-80 transition-opacity duration-300 ease-in-out"
                fill="none" 
                strokeWidth="2" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path d="M3 7.8V18a2 2 0 002 2h14a2 2 0 002-2V7.8m-9 6.2V4m0 10l-3-3m3 3l3-3" />
              </svg>
            )}
            <p className="text-black text-sm font-medium opacity-0 group-hover:opacity-80 transition-opacity duration-300 ease-in-out text-center">
              Click to read your {isEmail ? 'email' : 'letter'}
            </p>
          </div>
        )}
      </div>
    );
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const profileResponse = await fetch(`${API_BASE_URL}/api/auth/profile`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!profileResponse.ok) {
          if (profileResponse.status === 401) {
            logout();
            navigate('/login');
            return;
          }
          throw new Error('Failed to fetch profile data');
        }

        const profileData = await profileResponse.json();
        setProfile(profileData.user);

        const mailResponse = await fetch(`${API_BASE_URL}/api/letters/userpreview/${profileData.user.user_id}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );

        if (!mailResponse.ok) {
          throw new Error('Failed to fetch mail items');
        }

        const mailData = await mailResponse.json();
        setMailItems(mailData.items);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token, logout, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 p-4 rounded-lg">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">{error}</h3>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!profile) return null;

  return (
    <main className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header section */}
        <div className="bg-white shadow rounded-lg mb-6 p-4">
          <div className="flex justify-between items-center">
            <div>
            </div>
            <div className="flex space-x-4">
              <button
                onClick={() => navigate('/write')}
                className="px-6 py-3 bg-primary text-white text-lg rounded-md hover:bg-accent focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 shadow-sm"
              >
                Write a Letter
              </button>
            </div>
          </div>
        </div>

        {/* Mail Items section */}
        <div className="bg-white shadow rounded-lg p-6">


          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {mailItems.length === 0 ? (
              <div className="col-span-full bg-gray-50 rounded-lg p-8 flex flex-col items-center justify-center text-center">
                <Mail className="w-16 h-16 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No mail yet</h3>
                <p className="text-sm text-gray-500 mb-4">You haven't sent any mail yet. Start by writing your first letter or email!</p>
                <button
                  onClick={() => navigate('/write')}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                >
                  Write Now
                </button>
              </div>
            ) : (
              mailItems.map(item => renderMailItem(item))
            )}
          </div>
        </div>
      </div>
    </main>
  );
};

export default DashboardPage;